import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Section } from '../components/Section';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { ShoppingCart, Users, DollarSign, Check, Zap, Download, FileSpreadsheet, Globe } from 'lucide-react';
export function Features() {
  const [activeTab, setActiveTab] = useState<'capture' | 'export' | 'integrate'>('capture');
  const tabs = [{
    id: 'capture',
    label: 'Cart Capture',
    icon: ShoppingCart
  }, {
    id: 'export',
    label: 'Export Options',
    icon: FileSpreadsheet
  }, {
    id: 'integrate',
    label: 'Integrations',
    icon: Globe
  }];
  const content = {
    capture: [{
      title: 'One-Click Capture',
      desc: 'Extract all cart details with a single click from any supported website.',
      icon: Zap
    }, {
      title: 'Complete Item Data',
      desc: 'Captures product names, SKUs, quantities, prices, and descriptions automatically.',
      icon: ShoppingCart
    }, {
      title: 'Universal Support',
      desc: 'Works on any e-commerce website with a shopping cart.',
      icon: Globe
    }, {
      title: 'Real-Time Preview',
      desc: 'Review captured data before sending to ensure accuracy.',
      icon: Check
    }],
    export: [{
      title: 'Excel Export',
      desc: 'Download formatted spreadsheets ready to share with finance teams.',
      icon: FileSpreadsheet
    }, {
      title: 'Custom Templates',
      desc: "Create export templates that match your organization's format requirements.",
      icon: FileSpreadsheet
    }, {
      title: 'Batch Processing',
      desc: 'Capture and export multiple carts at once for efficiency.',
      icon: Zap
    }, {
      title: 'Email Integration',
      desc: 'Send exports directly via email to procurement or finance teams.',
      icon: Download
    }],
    integrate: [{
      title: 'API Access',
      desc: 'Send cart data directly to your procurement system via REST API.',
      icon: Globe
    }, {
      title: 'Webhook Support',
      desc: 'Configure webhooks to push data to your internal systems automatically.',
      icon: Zap
    }, {
      title: 'Custom Fields',
      desc: "Map cart data to your system's specific field requirements.",
      icon: Check
    }, {
      title: 'Secure Transfer',
      desc: 'All data transfers are encrypted and secure.',
      icon: Check
    }]
  };
  return <div className="pt-20" data-id="element-155">
      <Section className="bg-[#6366F1] text-white pb-32" data-id="element-156">
        <div className="container mx-auto px-4 text-center" data-id="element-157">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-158">
            Powerful Features for Every Workflow
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto" data-id="element-159">
            From cart capture to data delivery, Cart Tracker handles it all.
          </p>
        </div>
      </Section>

      <div className="container mx-auto px-4 -mt-20 relative z-10" data-id="element-160">
        <div className="bg-white rounded-2xl shadow-xl p-2 md:p-4 mb-12 flex flex-col md:flex-row justify-center gap-2 md:gap-4 max-w-3xl mx-auto" data-id="element-161">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={`flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-lg font-semibold transition-all duration-200 flex-1 ${activeTab === tab.id ? 'bg-[#6366F1] text-white shadow-md' : 'bg-transparent text-gray-500 hover:bg-gray-50'}`} data-id="element-162">
              <tab.icon className="w-5 h-5" data-id="element-163" />
              {tab.label}
            </button>)}
        </div>

        <AnimatePresence mode="wait" data-id="element-164">
          <motion.div key={activeTab} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20" data-id="element-165">
            {content[activeTab].map((feature, i) => <Card key={i} className="p-8 flex items-start gap-6" hoverEffect data-id="element-166">
                <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0" data-id="element-167">
                  <feature.icon className="w-6 h-6 text-[#6366F1]" data-id="element-168" />
                </div>
                <div data-id="element-169">
                  <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-170">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-171">
                    {feature.desc}
                  </p>
                </div>
              </Card>)}
          </motion.div>
        </AnimatePresence>
      </div>

      <Section className="bg-gradient-to-br from-[#EEF2FF] to-white" data-id="element-172">
        <div className="container mx-auto px-4 text-center" data-id="element-173">
          <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-174">
            Ready to streamline your process?
          </h2>
          <div className="flex justify-center gap-4" data-id="element-175">
            <Button variant="primary" data-id="element-176">Install Extension</Button>
            <Button variant="outline" onClick={() => window.open('https://calendly.com/alex-thruline/cart-tracker', '_blank')} data-id="element-177">
              Schedule Demo
            </Button>
          </div>
        </div>
      </Section>
    </div>;
}